import * as THREE from 'three'
import type {
  CapsuleCharacterMotorOptions,
  CharacterHandle,
  CharacterLookInteraction,
  CharacterSnapshot,
  CharacterThirdPersonViewBindingOptions,
  PhaseRenderContext,
  PointerLockService,
  PotreeEngine,
  RenderContributor,
  SpatialCapsuleClickToWalkController,
  SpatialCapsuleClickToWalkSnapshot,
  ViewFrameContext,
  ViewHandle,
} from '@sharefe/share3d-engine'
import { RenderPhase } from '@/shared/types/assets'
import type { RenderPipeline } from '@/shared/types/services'
import { SpatialCapsuleClickToWalkIndicators } from '@/plugins/character/navigation/SpatialCapsuleClickToWalkIndicators'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import { ThrownBallPool } from './ThrownBallPool'
import { resetPlayerOutOfBounds } from './runtimeUtils'

/** @internal */
export type SpatialCapsuleWalkMode = 'first-person' | 'third-person'

/** @internal */
export interface SpatialCapsuleWalkRuntimeState {
  mode: SpatialCapsuleWalkMode
  lookInteraction: CharacterLookInteraction
  pointerLocked: boolean
  grounded: boolean
  speed: number
  activeBalls: number
  position: THREE.Vector3
  velocity: THREE.Vector3
  stepHeight: number
  observerViewVisible: boolean
  observerViewId: string | null
  clickToWalk: SpatialCapsuleClickToWalkSnapshot
}

/** @internal */
export interface SpatialCapsuleWalkRuntimeOptions {
  engine: PotreeEngine
  container: HTMLElement
  worldId: string
  spawnFloorPoint: THREE.Vector3
  defaultMode?: SpatialCapsuleWalkMode
  defaultLookInteraction?: CharacterLookInteraction
  observerViewVisible?: boolean
  clickToWalk?: SpatialCapsuleClickToWalkController
  onStateChange?: (state: SpatialCapsuleWalkRuntimeState) => void
}

const DEFAULT_LOOK_DIRECTION = new THREE.Vector3(0, 1, 0)
const DEFAULT_CHARACTER_ID = 'spatial-capsule-walk-player'
const DEFAULT_OBSERVER_VIEW_ID = 'spatial-capsule-walk-observer'
const OBSERVER_VIEW_MARGIN = 24
const OBSERVER_VIEW_MIN_WIDTH = 240
const OBSERVER_VIEW_MAX_WIDTH = 420
const OBSERVER_VIEW_MIN_HEIGHT = 160
const OBSERVER_VIEW_MAX_HEIGHT = 280
const UNITY_STARTER_ASSETS_WALK_SPEED = 2
const UNITY_STARTER_ASSETS_SPRINT_SPEED = 5.335
const UNITY_STARTER_ASSETS_SPEED_CHANGE_RATE = 10
const UNITY_STARTER_ASSETS_GRAVITY = 15
const UNITY_STARTER_ASSETS_JUMP_HEIGHT = 1.2
const UNITY_STARTER_ASSETS_FALL_TIMEOUT = 0.15
const SPATIAL_CAPSULE_WALK_MOTOR_OPTIONS = {
  groundControlMode: 'directional',
  groundContactGraceTime: UNITY_STARTER_ASSETS_FALL_TIMEOUT,
  groundProbeRadiusScale: 0.75,
  groundProbeSamples: 6,
  moveSpeed: UNITY_STARTER_ASSETS_WALK_SPEED,
  runMultiplier: UNITY_STARTER_ASSETS_SPRINT_SPEED / UNITY_STARTER_ASSETS_WALK_SPEED,
  jumpSpeed: Math.sqrt(
    UNITY_STARTER_ASSETS_JUMP_HEIGHT * 2 * UNITY_STARTER_ASSETS_GRAVITY,
  ),
  gravity: UNITY_STARTER_ASSETS_GRAVITY,
  groundAcceleration: UNITY_STARTER_ASSETS_SPEED_CHANGE_RATE,
} satisfies CapsuleCharacterMotorOptions
const SPATIAL_CAPSULE_WALK_CAMERA_COLLISION = {
  enabled: true,
  radius: 0.22,
  skinWidth: 0.08,
  minDistance: 0.85,
  pullInSmoothing: 24,
  restoreSmoothing: 8,
} satisfies NonNullable<CharacterThirdPersonViewBindingOptions['cameraCollision']>

/** @internal */
export class SpatialCapsuleWalkRuntime {
  private readonly pointerLockService: PointerLockService | null
  private readonly renderPipeline: RenderPipeline | null
  private readonly runtimeRoot: THREE.Group
  private readonly ballPool: ThrownBallPool
  private readonly primaryViewId: string
  private readonly onStateChange?: (state: SpatialCapsuleWalkRuntimeState) => void
  private readonly clickToWalk?: SpatialCapsuleClickToWalkController
  private readonly clickToWalkIndicators: SpatialCapsuleClickToWalkIndicators | null
  private readonly isolatedRuntimeScene = new THREE.Scene()
  private readonly renderContributor: RenderContributor = {
    kind: 'spatial-capsule-walk-runtime',
    phases: [RenderPhase.MainOcclusionTested],
    renderPriority: 12,
    render: (frame, _phase, renderContext) => {
      this.renderRuntime(frame, renderContext)
    },
  }

  private character: CharacterHandle | null = null
  private worldId: string
  private spawnFloorPoint: THREE.Vector3
  private mode: SpatialCapsuleWalkMode
  private lookInteraction: CharacterLookInteraction
  private observerViewVisible: boolean
  private observerViewId: string | null = null
  private disposed = false

  constructor(private readonly options: SpatialCapsuleWalkRuntimeOptions) {
    this.worldId = options.worldId
    this.spawnFloorPoint = options.spawnFloorPoint.clone()
    this.mode = options.defaultMode ?? 'third-person'
    this.lookInteraction = options.defaultLookInteraction ?? 'pointer-lock'
    this.observerViewVisible = options.observerViewVisible ?? false
    this.onStateChange = options.onStateChange
    this.clickToWalk = options.clickToWalk
    this.primaryViewId = options.engine.views.primaryViewId
    this.pointerLockService =
      options.engine.context.hasService('PointerLockService') ? 
        options.engine.context.getService('PointerLockService') :
        null
    this.renderPipeline =
      options.engine.context.hasService('RenderPipeline') ?
        options.engine.context.getService('RenderPipeline') :
        null

    const sceneRoot = options.engine.sceneGraph.registerRoot('spatial-capsule-walk')
    this.runtimeRoot = new THREE.Group()
    this.runtimeRoot.name = 'spatialCapsuleWalkRuntimeRoot'
    sceneRoot.add(this.runtimeRoot)

    this.ballPool = new ThrownBallPool(this.runtimeRoot, {
      overlapSphere: (sphere) =>
        this.options.engine.spatial.overlapCollisionSphere({
          worldId: this.worldId,
          sphere,
        }),
    })
    this.renderPipeline?.addRenderContributor(this.renderContributor)
    this.clickToWalkIndicators = this.clickToWalk
      ? new SpatialCapsuleClickToWalkIndicators({
          engine: this.options.engine,
          controller: this.clickToWalk,
        })
      : null

    this.syncObserverViewLifecycle()
    this.recreateCharacter()
    this.syncClickToWalkBinding()
    this.applyActiveModes({ requestPointerLock: false })
    this.options.engine.events.on('frame.end', this.handleFrameEnd)
    this.options.engine.events.on('viewport.resized', this.handleViewportResized)
    this.options.engine.events.on('view.removed', this.handleViewRemoved)
    this.options.container.addEventListener('pointerdown', this.handlePointerDown)
    window.addEventListener('keydown', this.handleKeyDown)
    this.emitState()
  }

  get pointerLocked(): boolean {
    return this.isPointerLocked()
  }

  setWorldId(worldId: string): void {
    if (this.worldId === worldId) {
      return
    }

    this.worldId = worldId
    this.ballPool.clear()
    this.recreateCharacter()
    this.syncClickToWalkBinding()
    this.applyActiveModes({ requestPointerLock: false })
    this.emitState()
  }

  setSpawnFloorPoint(point: THREE.Vector3, reset = false): void {
    this.spawnFloorPoint.copy(point)
    if (reset) {
      this.reset()
    }
  }

  enter(): void {
    this.setMode('first-person', {
      requestPointerLock: this.lookInteraction === 'pointer-lock',
    })
  }

  exit(): void {
    this.setMode('third-person')
  }

  setMode(
    mode: SpatialCapsuleWalkMode,
    options: {
      lookInteraction?: CharacterLookInteraction
      requestPointerLock?: boolean
    } = {},
  ): void {
    this.mode = mode
    if (options.lookInteraction) {
      this.lookInteraction = options.lookInteraction
    }

    this.applyBindings()
    this.syncClickToWalkBinding()
    this.applyActiveModes({
      requestPointerLock: options.requestPointerLock ?? false,
    })
    this.emitState()
  }

  setLookInteraction(
    lookInteraction: CharacterLookInteraction,
    requestPointerLock = lookInteraction === 'pointer-lock',
  ): void {
    this.lookInteraction = lookInteraction
    this.applyBindings()
    this.syncClickToWalkBinding()

    if (this.mode === 'first-person') {
      this.applyActiveModes({ requestPointerLock })
    } else if (lookInteraction !== 'pointer-lock') {
      this.pointerLockService?.exit()
      this.emitState()
    }
  }

  setObserverViewVisible(visible: boolean): void {
    if (this.observerViewVisible === visible) {
      this.syncObserverViewRect()
      this.emitState()
      return
    }

    this.observerViewVisible = visible
    this.syncObserverViewLifecycle()
    this.applyBindings()
    this.applyActiveModes({ requestPointerLock: false })
    this.emitState()
  }

  reset(): void {
    const snapshot = this.character?.setFloorPoint(this.spawnFloorPoint)
    this.clickToWalk?.bind(null)
    this.syncClickToWalkBinding()
    this.emitState(snapshot)
  }

  clearBalls(): void {
    this.ballPool.clear()
    this.emitState()
  }

  throwBall(): void {
    const snapshot = this.character?.getSnapshot()
    if (!snapshot) {
      return
    }

    this.ballPool.throw(
      snapshot.eyePosition,
      createLookDirection(snapshot),
      snapshot.velocity,
    )
    this.emitState(snapshot)
  }

  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    this.clickToWalk?.bind(null)
    this.options.engine.events.off('frame.end', this.handleFrameEnd)
    this.options.engine.events.off('viewport.resized', this.handleViewportResized)
    this.options.engine.events.off('view.removed', this.handleViewRemoved)
    this.options.container.removeEventListener('pointerdown', this.handlePointerDown)
    window.removeEventListener('keydown', this.handleKeyDown)
    this.pointerLockService?.exit()
    this.renderPipeline?.removeRenderContributor(this.renderContributor)
    this.ballPool.dispose()
    this.clickToWalkIndicators?.dispose()
    this.character?.dispose()
    this.character = null
    this.removeObserverView()
    this.runtimeRoot.removeFromParent()
  }

  private recreateCharacter(): void {
    const command = this.resolveInitialCommand()
    this.character?.dispose()
    this.character = this.options.engine.characters.create({
      id: DEFAULT_CHARACTER_ID,
      floorPoint: this.spawnFloorPoint,
      spatialQuery: {
        worldId: this.worldId,
      },
      motorOptions: SPATIAL_CAPSULE_WALK_MOTOR_OPTIONS,
      command,
    })
    this.applyBindings()
    this.syncClickToWalkBinding()
  }

  private resolveInitialCommand(): {
    bodyYaw: number
    aimYaw: number
    aimPitch: number
  } {
    const currentCommand = this.character?.getCommand()
    if (currentCommand) {
      return {
        bodyYaw: currentCommand.bodyYaw,
        aimYaw: currentCommand.aimYaw,
        aimPitch: currentCommand.aimPitch,
      }
    }

    const rig =
      this.options.engine.views.get(this.primaryViewId)?.getCameraRig() ??
      this.options.engine.camera
    const state = rig.getState()
    const lookDirection = state.target.clone().sub(state.position)
    if (lookDirection.lengthSq() <= Number.EPSILON) {
      return {
        bodyYaw: Math.atan2(DEFAULT_LOOK_DIRECTION.y, DEFAULT_LOOK_DIRECTION.x),
        aimYaw: Math.atan2(DEFAULT_LOOK_DIRECTION.y, DEFAULT_LOOK_DIRECTION.x),
        aimPitch: 0,
      }
    }

    lookDirection.normalize()
    const aimYaw = Math.atan2(lookDirection.y, lookDirection.x)
    const aimPitch = Math.asin(THREE.MathUtils.clamp(lookDirection.z, -1, 1))
    return {
      bodyYaw: aimYaw,
      aimYaw,
      aimPitch,
    }
  }

  private applyBindings(): void {
    if (!this.character) {
      return
    }

    this.options.engine.characters.bindViewCharacter(this.primaryViewId, this.character.id, {
      firstPerson: {
        lookInteraction: this.lookInteraction,
      },
      thirdPerson: {
        followDistance: 4.8,
        cameraCollision: SPATIAL_CAPSULE_WALK_CAMERA_COLLISION,
      },
    })

    if (this.observerViewId) {
      this.options.engine.characters.bindViewCharacter(this.observerViewId, this.character.id, {
        thirdPerson: {
          followDistance: 7.5,
          minDistance: 4.5,
          maxDistance: 18,
          cameraCollision: {
            ...SPATIAL_CAPSULE_WALK_CAMERA_COLLISION,
            minDistance: 2.4,
          },
        },
      })
    }
  }

  private applyActiveModes(options: { requestPointerLock: boolean }): void {
    const primaryView = this.getPrimaryView()
    if (primaryView) {
      primaryView.requireControlManager().setMode(
        this.mode === 'first-person' ?
          'character-first-person' :
          'character-third-person',
      )
    }

    if (this.mode !== 'first-person' || this.lookInteraction !== 'pointer-lock') {
      this.pointerLockService?.exit()
    } else if (options.requestPointerLock) {
      this.pointerLockService?.request(this.primaryViewId)
    }

    const observerView = this.getObserverView()
    if (observerView) {
      observerView.requireControlManager().setMode('character-third-person')
    }
  }

  private syncObserverViewLifecycle(): void {
    if (!this.observerViewVisible) {
      this.removeObserverView()
      return
    }

    const existing = this.getObserverView()
    if (existing) {
      existing.setVisible(true)
      existing.clearSlot('renderPolicy')
      this.syncObserverViewRect()
      this.observerViewId = existing.id
      return
    }

    const observerView = this.options.engine.views.create({
      id: DEFAULT_OBSERVER_VIEW_ID,
      rect: this.createObserverViewRect(),
      zIndex: 20,
      visible: true,
      userData: {
        role: 'observer',
      },
    })
    this.observerViewId = observerView.id
    this.syncObserverViewRect()
  }

  private removeObserverView(): void {
    if (!this.observerViewId) {
      return
    }

    this.options.engine.characters.unbindViewCharacter(this.observerViewId)
    this.options.engine.views.remove(this.observerViewId)
    this.observerViewId = null
  }

  private getPrimaryView(): ViewHandle | null {
    return this.options.engine.views.get(this.primaryViewId) ?? null
  }

  private getObserverView(): ViewHandle | null {
    if (!this.observerViewId) {
      return null
    }

    return this.options.engine.views.get(this.observerViewId) ?? null
  }

  private syncClickToWalkBinding(): void {
    this.clickToWalk?.bind({
      worldId: this.worldId,
      primaryViewId: this.primaryViewId,
      mode: this.mode,
      lookInteraction: this.lookInteraction,
      character: this.character,
      getPrimaryCamera: () =>
        this.getPrimaryView()?.getViewportCamera()?.getActiveCamera() ?? null,
      isPointerLocked: () => this.isPointerLocked(),
    })
  }

  private createObserverViewRect(): {
    x: number
    y: number
    width: number
    height: number
  } {
    const width = this.options.container.clientWidth || this.options.engine.context.getSize().width
    const height =
      this.options.container.clientHeight || this.options.engine.context.getSize().height
    const rectWidth = THREE.MathUtils.clamp(
      Math.round(width * 0.28),
      OBSERVER_VIEW_MIN_WIDTH,
      OBSERVER_VIEW_MAX_WIDTH,
    )
    const rectHeight = THREE.MathUtils.clamp(
      Math.round(height * 0.26),
      OBSERVER_VIEW_MIN_HEIGHT,
      OBSERVER_VIEW_MAX_HEIGHT,
    )

    return {
      x: Math.max(16, width - rectWidth - OBSERVER_VIEW_MARGIN),
      y: OBSERVER_VIEW_MARGIN,
      width: rectWidth,
      height: rectHeight,
    }
  }

  private syncObserverViewRect(): void {
    const observerView = this.getObserverView()
    if (!observerView) {
      return
    }

    observerView.setRect(this.createObserverViewRect())
  }

  private resetOutOfBoundsIfNeeded(snapshot: CharacterSnapshot): CharacterSnapshot {
    if (!this.character) {
      return snapshot
    }

    return resetPlayerOutOfBounds(snapshot, this.character, this.spawnFloorPoint)
  }

  private isPointerLocked(): boolean {
    return this.pointerLockService?.isLockedFor(this.primaryViewId) ?? false
  }

  private emitState(snapshot = this.character?.getSnapshot()): void {
    const activeSnapshot = snapshot
    this.onStateChange?.({
      mode: this.mode,
      lookInteraction: this.lookInteraction,
      pointerLocked: this.isPointerLocked(),
      grounded: activeSnapshot?.grounded ?? false,
      speed: activeSnapshot?.speed ?? 0,
      activeBalls: this.ballPool.getSnapshot().activeCount,
      position: activeSnapshot?.capsule.start.clone() ?? new THREE.Vector3(),
      velocity: activeSnapshot?.velocity.clone() ?? new THREE.Vector3(),
      stepHeight: activeSnapshot?.stepHeight ?? 0,
      observerViewVisible: this.observerViewVisible,
      observerViewId: this.observerViewId,
      clickToWalk: this.clickToWalk?.getSnapshot() ?? createEmptyClickToWalkSnapshot(),
    })
  }

  private renderRuntime(
    frame: ViewFrameContext,
    renderContext: PhaseRenderContext,
  ): void {
    if (this.runtimeRoot.children.length === 0) {
      return
    }

    // 运行时挂在独立 Scene 上以隔离主场景；View 状态与 render target 由 backend 依当前 View 决定。
    const restoreParent = this.attachRuntimeRootToIsolatedScene()
    try {
      submitSceneInView(renderContext, this.isolatedRuntimeScene, frame.camera)
    } finally {
      restoreParent()
    }
  }

  private attachRuntimeRootToIsolatedScene(): () => void {
    const originalParent = this.runtimeRoot.parent
    this.isolatedRuntimeScene.add(this.runtimeRoot)
    return () => {
      this.runtimeRoot.removeFromParent()
      if (originalParent) {
        originalParent.add(this.runtimeRoot)
      }
    }
  }

  private readonly handleFrameEnd = (event: {
    engineFrame: { delta: number }
  }) => {
    if (this.disposed || !this.character) {
      return
    }

    const delta = event.engineFrame.delta
    const snapshot = this.resetOutOfBoundsIfNeeded(this.character.getSnapshot())
    this.ballPool.update(delta, snapshot.capsule)
    this.emitState(snapshot)
  }

  private readonly handleViewportResized = () => {
    if (!this.observerViewVisible) {
      return
    }

    this.syncObserverViewRect()
  }

  private readonly handleViewRemoved = ({ viewId }: { viewId: string }) => {
    if (viewId !== this.observerViewId) {
      if (viewId === this.primaryViewId) {
        this.clickToWalk?.bind(null)
      }
      return
    }

    this.observerViewId = null
    this.observerViewVisible = false
    this.emitState()
  }

  private readonly handlePointerDown = (event: PointerEvent) => {
    if (!this.isPointerLocked() || event.button !== 0) {
      return
    }

    this.throwBall()
  }

  private readonly handleKeyDown = (event: KeyboardEvent) => {
    if (event.repeat || event.code !== 'KeyF') {
      return
    }

    this.throwBall()
  }
}

function createEmptyClickToWalkSnapshot(): SpatialCapsuleClickToWalkSnapshot {
  return {
    available: false,
    active: false,
    hoverPoint: null,
    hoverNormal: null,
    hoverScreen: null,
    targetPoint: null,
    targetNormal: null,
    targetScreen: null,
    lastReason: null,
  }
}

function createLookDirection(snapshot: CharacterSnapshot): THREE.Vector3 {
  const cosPitch = Math.cos(snapshot.aimPitch)
  return new THREE.Vector3(
    Math.cos(snapshot.aimYaw) * cosPitch,
    Math.sin(snapshot.aimYaw) * cosPitch,
    Math.sin(snapshot.aimPitch),
  ).normalize()
}
